import { Logger } from '@gitbook/util-debug';
import { createCache } from '@gitbook/util-suspense-cache';

import { apiClient } from './api';

export interface SearchRequest {
    organizationId: string;
    query: string;
}

const logger = Logger('search');

export type InAppSearchResult =
    | {
          index: 'pages';
          object: {
              title: string;
              space: string;
              page: string;
              organization?: string;
          };
      }
    | {
          index: 'sections';
          object: {
              objectID: string;
              title: string;
              body: string;
              space: string;
              page: string;
              anchor: string;
              organization?: string;
          };
      };

/**
 * Suspense cache to access search results.
 */
export const searchCache = createCache<SearchRequest, ReadonlyArray<InAppSearchResult>>({
    // Clear results from API after 10 seconds to get up-to-date results
    ttl: 10000,
    getValue: async ({ query, organizationId }) => {
        if (!query) {
            return [];
        }

        logger.debug('search', { query, organizationId });

        const response = await apiClient.organizations.searchOrganizationContent(organizationId, {
            query,
        });

        const items = response.data?.items || [];
        const entries: InAppSearchResult[] = [];

        for (const spaceResult of items) {
            const spaceId = spaceResult.id;
            const pages = spaceResult.pages || [];

            for (const page of pages) {
                entries.push({
                    index: 'pages',
                    object: {
                        space: spaceId,
                        page: page.id,
                        organization: organizationId,
                        title: page.title,
                    },
                });

                // Map sections for this page, if any
                const sections = page.sections || [];
                for (const section of sections) {
                    // `anchor` isn't included in the API response, but it's available in the path.
                    // See services/cloudrun/api/src/operations/search/searchContent.ts#groupSectionsByPage
                    const hashIndex = section.path.indexOf('#');
                    const anchor = hashIndex !== -1 ? section.path.substring(hashIndex + 1) : '';

                    entries.push({
                        index: 'sections',
                        object: {
                            objectID: section.id,
                            space: spaceId,
                            page: page.id,
                            organization: organizationId,
                            title: section.title,
                            body: section.body,
                            anchor,
                        },
                    });
                }
            }
        }

        return entries;
    },
    getKey: ({ query, organizationId }) => `${organizationId}:${query}`,
});
